import Dexie, { Table } from 'dexie';
import { Kit, ProductInventory } from '@core/core.types';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  LaboratoryBatch,
  LaboratoryBatchAction,
  LaboratoryBatchSample,
} from '@app/modules/laboratory/laboratory.types';
import { Sample } from '@app/modules/service-data/types/sample.types';

export class AppDB extends Dexie {
  samples!: Table<Sample, string>;
  kits!: Table<Kit, string>;
  piis!: Table<ProductInventory, string>;
  laboratoryBatches!: Table<LaboratoryBatch, string>;
  laboratoryBatchActions!: Table<LaboratoryBatchAction, string>;

  stateUpdatedSubject = new BehaviorSubject<boolean>(true);

  constructor() {
    super('LaboratoryDatabase');
    this.version(1).stores({
      samples: 'id,kitId, dateOfVenipuncture',
      kits: 'id',
      piis: 'id,kitId',
      laboratoryBatches: 'id,status',
      laboratoryBatchActions: 'id,kitId,batchId,timestamp',
    });
  }

  deleteOldData() {
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

    return combineLatest([
      from(
        this.laboratoryBatchActions
          .where('timestamp')
          .below(fourteenDaysAgo)
          .delete()
      ),

      from(this.kits.clear()),
      from(this.samples.clear()),
      from(this.piis.clear()),
    ]);
  }

  addSamplesList(samples: Sample[]) {
    return from(this.samples.bulkPut(samples));
  }

  addKitsList(kits: Kit[]) {
    return from(this.kits.bulkPut(kits));
  }

  addPiisList(piis: ProductInventory[]) {
    return from(this.piis.bulkPut(piis));
  }

  addLaboratoryBatchAction(action: LaboratoryBatchAction) {
    return from(this.laboratoryBatchActions.put(action));
  }

  addLaboratoryBatch(batch: LaboratoryBatch) {
    return from(this.laboratoryBatches.put(batch)).pipe(
      tap(() => this.stateUpdatedSubject.next(true))
    );
  }

  clearBatchActions(batchId: string) {
    return from(
      this.laboratoryBatchActions
        .where('batchId')
        .equals(batchId)
        .and((action) => !action.synced)
        .and((action) => !action.deleted)
        .modify({ deleted: true })
    );
  }

  markBatchActionsAsSynced(batchId: string) {
    return from(
      this.laboratoryBatchActions
        .where('batchId')
        .equals(batchId)
        .and((action) => !action.synced)
        .and((action) => !action.deleted)
        .modify({ synced: true })
    );
  }

  getLaboratoryBatch(batchId: string) {
    return this.stateUpdatedSubject.pipe(
      switchMap(() => from(this.laboratoryBatches.get(batchId)))
    );
  }

  getBatchSampleData(
    batchId: string,
    kitId: string
  ): Observable<LaboratoryBatchSample> {
    return from(this.laboratoryBatches.get(batchId)).pipe(
      map((batch) => {
        let sample = batch.samples.find((sample) => sample.kitId === kitId);
        if (!sample) {
          sample = batch.dismissedSamples.find(
            (sample) => sample.kitId === kitId
          );
          if (sample) {
            sample.removed = true;
          }
        }
        return sample;
      })
    );
  }

  getLaboratoryBatchActions(batchId: string) {
    return this.stateUpdatedSubject.pipe(
      switchMap(() =>
        from(
          this.laboratoryBatchActions
            .where('batchId')
            .equals(batchId)
            .and((action) => !action.synced)
            .and((action) => !action.deleted)
            .toArray()
        )
      )
    );
  }
}

export const db = new AppDB();
